import React, { use, useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import isEqual from "lodash/isEqual";
import {
    Keyboard,
    LayoutRectangle,
    Platform,
    StyleSheet,
    TextInput as RNTextInput,
    useWindowDimensions,
} from "react-native";
import { FullWindowOverlay } from "react-native-screens";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { PortalHost } from "@gorhom/portal";
import { useDebouncedCallback } from "use-debounce";
import { useTheme } from "../../hooks/use-theme";
import { KeyboardTypeOptions, StyleProp, ViewStyle } from "react-native";
import VStack from "../../ui_atoms/VStack";
import ListItem, { ListItemProps } from "../../ui_atoms/ListItem";
import { processSelectItems, sleep } from "../../utils";
import TextField from "../TextField";
import Loading from "../Loading";
import Animated, {
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
} from "react-native-reanimated";
import { SystemBars } from "react-native-edge-to-edge";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
    BottomSheetBackdrop,
    BottomSheetBackdropProps,
    BottomSheetModal,
    BottomSheetVirtualizedList,
    useBottomSheetTimingConfigs,
} from "@gorhom/bottom-sheet";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import {
    KEYBOARD_STICKY_COMPONENT_ANIMATION_DELAY,
    KEYBOARD_STICKY_COMPONENT_HOST_NAME,
    KeyboardStickyComponentManagerContext,
} from "../../contexts/KeyboardStickyComponentManager";
import HStack from "../../ui_atoms/HStack";
import IconButton from "../IconButton";
import { useKeyboardState, useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import { BackgroundBlur } from "../BackgroundBlur";
import { useIsFocused } from "@react-navigation/core";
import * as Device from "expo-device";

const topInsetExcludedFromWindow =
    Platform.OS !== "android" ||
    (typeof Device.platformApiLevel === "number" && Device.platformApiLevel >= 35);

const AnimatedHStack = Animated.createAnimatedComponent(HStack);

const BACKGROUND_RGB = "26, 21, 54";

const DEFAULT_HANDLE_CONTAINER_PADDING = 18;
const MAX_BOTTOM_SHEET_WIDTH = 700;

// How long to wait, once the sheet physically sits at the closed position, for the modal's
// onDismiss to arrive before concluding the close events were dropped (see the animated
// reaction below). In a normal close, onDismiss fires within a frame or two of the index
// reaching -1, so this only needs to cover that gap.
const DROPPED_DISMISS_GRACE_PERIOD_MS = 250;

// On iOS the sheet is rendered inside a native full-window overlay so it appears ABOVE native modals
// (e.g. the CreateMissionStack native iOS modal, which otherwise covers any JS overlay). The overlay
// is a separate native window outside the root GestureHandlerRootView, so it needs its own one for
// the backdrop tap / pan-to-close gestures to work. On Android/web this is left to the default
// container, where JS overlays already render above everything.
// The PortalHost, rendered AFTER the sheet in the same overlay, receives the search TextField from
// KeyboardStickyComponentManager: mounted at the root it would paint behind this overlay and behind
// native modals (BAS-2851).
// `box-none` so the overlay itself never swallows a touch: the sheet, its backdrop and the search
// field take the ones they want, and everything else reaches the screen underneath. The overlay
// spans the whole window for as long as the modal is mounted, so without this a sheet that ends up
// invisible — closed by the library without telling us — would leave the app unusable.
const IOSFullWindowOverlayContainer = ({ children }: React.PropsWithChildren) => (
    <FullWindowOverlay>
        <GestureHandlerRootView style={StyleSheet.absoluteFill} pointerEvents="box-none">
            {children}
            <PortalHost name={KEYBOARD_STICKY_COMPONENT_HOST_NAME} />
        </GestureHandlerRootView>
    </FullWindowOverlay>
);

const bottomSheetContainerComponent =
    Platform.OS === "ios" ? IOSFullWindowOverlayContainer : undefined;

export type SelectItemType<V> = {
    value: V;
    label: string;
} & ListItemProps;

export type SearchItemsFunction<V> = (
    filter: string,
) => Promise<SelectItemType<V>[] | null> | SelectItemType<V>[] | null;

export const isSelectItemType = <V,>(value: unknown): value is SelectItemType<V> =>
    !!(value as SelectItemType<V>).value;

export type SelectChooserProps<V> = {
    onSelect?: (selectedItem: SelectItemType<V>) => void | Promise<void>;
    searchItems: SearchItemsFunction<V> | SelectItemType<V>[] | Promise<SelectItemType<V>[]> | null;
    onDismiss: () => void;
    visible: boolean;
    placeholder?: string;
    emptyResult?: JSX.Element;
    selectedValue?: V;
    searchKeyboardType?: KeyboardTypeOptions;
    isDynamicHeight?: boolean;
    selectable?: boolean;
    topComponent?: React.ReactNode;
    topComponentStyle?: StyleProp<ViewStyle>;
    bottomComponent?: React.ReactNode;
    lightMode?: boolean;
    disabled?: boolean;
    isLegacyLabel?: boolean;
    useDebounce?: boolean;
    autoFocus?: boolean;
    dismissOnPress?: boolean; // Closes the SelectChooser before calling an item's onPress();
};

export const SelectChooser = <V,>(
    {
        onSelect,
        searchItems,
        onDismiss,
        visible,
        placeholder,
        emptyResult,
        selectedValue,
        searchKeyboardType,
        selectable = true,
        topComponent,
        topComponentStyle,
        bottomComponent,
        lightMode = false,
        disabled = false,
        isLegacyLabel = true,
        useDebounce = false,
        dismissOnPress = true,
        autoFocus = true,
    }: SelectChooserProps<V>, // Generic type V is used correctly here
) => {
    const { colors, gridSize, roundness } = useTheme();
    const [searchQuery, setSearchQuery] = React.useState("");
    const [isSearching, setIsSearching] = React.useState(false);
    const showEmptytext = searchQuery.length < 3;
    const [items, setItems] = React.useState<SelectItemType<V>[]>(
        Array.isArray(searchItems) ? searchItems : [],
    );
    // Resync `items` whenever the parent provides a new static `searchItems`
    // prop. Uses the "adjust state on prop change" pattern. The lazy
    // initializer / updater form is required because `searchItems` may itself
    // be a function — passing it bare would make React treat it as the
    // updater and call it instead of storing it.
    const [prevSearchItems, setPrevSearchItems] = useState<SelectChooserProps<V>["searchItems"]>(
        () => searchItems,
    );
    if (searchItems !== prevSearchItems) {
        setPrevSearchItems(() => searchItems);
        if (Array.isArray(searchItems)) {
            setItems(searchItems);
        }
    }
    const textInput = React.useRef<RNTextInput | null>(null);
    const [isStaticItems, setIsStaticItems] = useState(Array.isArray(searchItems));
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const isLarge = useIsScreenSizeMin("large");

    const debouncedProcessSelectItems = useDebouncedCallback(processSelectItems<V>, 1000);
    React.useEffect(() => {
        let canceled = false;
        // we want to be sure we are re-rendering items when select become visible
        if (visible && !Array.isArray(searchItems)) {
            setIsSearching(true);
            if (useDebounce) {
                debouncedProcessSelectItems(
                    searchItems,
                    searchQuery,
                    (newItems: SelectItemType<V>[]) => {
                        if (!canceled) {
                            setItems(newItems);
                            setIsSearching(false);
                        }
                    },
                );
            } else {
                const { isStaticItems: _isStaticItems } = processSelectItems(
                    searchItems,
                    searchQuery,
                    (newItems) => {
                        if (!canceled) {
                            setItems(newItems);
                            setIsSearching(false);
                        }
                    },
                );

                setIsStaticItems(_isStaticItems);
            }
        }
        return () => {
            canceled = true;
        };
    }, [searchQuery, debouncedProcessSelectItems, searchItems, useDebounce, visible]);

    // Callers pass `onDismiss` inline, so it is a new function on every render of their screen.
    // Kept behind a ref so `onClose` — and everything memoised on it, `renderBackdrop` above all
    // — stays the same function for the life of the chooser. See the note there.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    const onClose = useCallback(() => {
        onDismissRef.current();
        void setSearchQuery("");
        Keyboard.dismiss();
    }, []);

    const rowRenderer = useCallback(
        ({ item }: { item: SelectItemType<V> }) => {
            const { label, value, onPress, ...itemProps } = item;
            const checked = label === selectedValue || isEqual(value, selectedValue);
            return (
                <ListItem
                    selectable={selectable}
                    style={{ width: "100%" }}
                    {...(isLegacyLabel
                        ? { title: label }
                        : { title: itemProps.title, labels: [{ text: label }] })}
                    {...itemProps}
                    onPress={async () => {
                        if (dismissOnPress) {
                            onClose();
                            await sleep(0);
                        }
                        if (onPress) {
                            await onPress();
                        } else {
                            await onSelect?.(item);
                        }
                    }}
                    {...(checked
                        ? {
                              checked: lightMode ? "light" : true,
                          }
                        : {})}
                />
            );
        },
        [selectedValue, selectable, isLegacyLabel, lightMode, dismissOnPress, onClose, onSelect],
    );

    const setStatusBarLight = useCallback(() => {
        SystemBars.setStyle("light");
    }, []);

    const setStatusBarDark = useCallback(() => {
        SystemBars.setStyle("dark");
    }, []);

    const bottomSheetModalRef = useRef<BottomSheetModal>(null);
    // Tracks whether the sheet is currently presented so the `visible` effect only
    // dismisses an actually-open sheet. Guards against gorhom/bottom-sheet#2669:
    // calling dismiss() while the modal is not presented (before the first present,
    // or after it already closed itself via swipe/backdrop) leaves its statusRef
    // stuck in DISMISSING and blocks every subsequent present().
    const isPresentedRef = useRef(false);
    // Remounting the modal (key bump) is the only safe way to discard an instance whose close
    // events were dropped: its internal statusRef is unknown and its iOS full-window overlay
    // must be torn down. dismiss() on such an instance no-ops (the sheet is already at the
    // closed position) and would strand its status in DISMISSING, blocking future present().
    const [modalInstanceId, setModalInstanceId] = useState(0);
    // Give every open request a brand new BottomSheetModal.
    //
    // The sheet is dynamically sized, so @gorhom/bottom-sheet opens it from a reaction to its
    // computed detents changing, which early-exits when they are unchanged and the sheet has
    // already animated on mount once (BottomSheet.tsx, "OnSnapPointsChange"). Dismissing does
    // not remount the inner BottomSheet, so its shared values survive: a chooser whose content
    // is identical every time — the language list is always the same two rows — recomputes the
    // exact same detents on reopen, the reaction bails out, and the sheet silently stays at its
    // closed position. Only an unrelated sheet closing on top of it would move it, by way of
    // the modal stack calling snapToIndex() directly, which is why opening and closing another
    // chooser used to make this one appear (BAS-2846, reopened as BAS-2909).
    //
    // Remounting resets those shared values, so the detents are computed from scratch and the
    // open animation runs. Uses the "adjust state on prop change" pattern rather than an effect
    // so the new instance is mounted before the presentation effect below runs, otherwise
    // present() would land on the instance being torn down.
    const [wasVisible, setWasVisible] = useState(visible);
    if (visible !== wasVisible) {
        setWasVisible(visible);
        if (visible) {
            setModalInstanceId((id) => id + 1);
        }
    }
    // Written by the sheet on the UI thread as the sheet moves, including during the pan
    // gesture itself — unlike onChange/onClose/onDismiss, it cannot be dropped.
    const sheetAnimatedIndex = useSharedValue(-1);
    const droppedDismissTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

    // Flip the presented flag off only once the modal has fully dismissed itself.
    // Kept separate from onClose (used by row/backdrop/icon presses) so a JS-driven
    // close still lets the effect below drive dismiss() while the sheet is open.
    const handleModalDismiss = useCallback(() => {
        clearTimeout(droppedDismissTimer.current);
        isPresentedRef.current = false;
        onClose();
    }, [onClose]);

    // The sheet reached the closed position: in a normal close, onDismiss follows within a
    // frame or two (handleModalDismiss cancels the timer). If it never comes, the close events
    // were dropped by @gorhom/bottom-sheet — e.g. a pan cancelled by a native recognizer only
    // runs onFinalize (no snap logic), or a pan released exactly at the closed position makes
    // animateToPosition exit before firing anything. Without this, `visible` stays true and
    // the SelectField stays disabled forever (BAS-2825).
    const handleSheetReachedClosedPosition = useCallback(() => {
        clearTimeout(droppedDismissTimer.current);
        droppedDismissTimer.current = setTimeout(() => {
            // Read the index again rather than trusting the reaction that armed this timer: a
            // sentinel -1 resolves back to the resting index within a few frames, and the UI
            // thread's "left the closed position" notification can land on a busy JS thread
            // after this timer already fired.
            if (!isPresentedRef.current || sheetAnimatedIndex.value !== -1) {
                return;
            }
            isPresentedRef.current = false;
            setModalInstanceId((id) => id + 1);
            onClose();
        }, DROPPED_DISMISS_GRACE_PERIOD_MS);
    }, [onClose, sheetAnimatedIndex]);

    const handleSheetLeftClosedPosition = useCallback(() => {
        clearTimeout(droppedDismissTimer.current);
    }, []);

    // `animatedIndex` is not only a position: @gorhom/bottom-sheet publishes -1 as a sentinel
    // whenever its layout is not (or no longer) calculated — while the container or the content
    // is being re-measured, its detents are empty and the index reads -1 even though the sheet
    // has not moved an inch (gorhom/react-native-bottom-sheet#1763). Taking that at face value
    // closed the filters sheet on its own, one grace period after every toggle (BAS-2894).
    // A sheet that genuinely reaches the closed position travels there, so its index walks
    // through the ]-1, 0[ range first; the sentinel jumps straight from the resting index. Only
    // the former arms the recovery above.
    useAnimatedReaction(
        () => sheetAnimatedIndex.value,
        (index: number, previousIndex: number | null) => {
            if (previousIndex === null || index === previousIndex) {
                return;
            }
            if (index !== -1) {
                if (previousIndex === -1) {
                    runOnJS(handleSheetLeftClosedPosition)();
                }
                return;
            }
            if (previousIndex > -1 && previousIndex < 0) {
                runOnJS(handleSheetReachedClosedPosition)();
            }
        },
        [handleSheetReachedClosedPosition, handleSheetLeftClosedPosition],
    );

    useEffect(() => () => clearTimeout(droppedDismissTimer.current), []);

    const { setKeyboardStickyComponent } = use(KeyboardStickyComponentManagerContext);
    const [keyboardStickyComponentLayout, setKeyboardStickyComponentLayout] =
        useState<LayoutRectangle>();

    useEffect(() => {
        if (visible && !disabled) {
            bottomSheetModalRef.current?.present();
            isPresentedRef.current = true;
            setStatusBarLight();
        } else {
            // Only dismiss a sheet that is still open. When it closes itself (swipe
            // down / backdrop tap) its onDismiss already ran and reset the modal to
            // INITIAL; calling dismiss() again would push it to DISMISSING while
            // unmounted and block every future present() (gorhom/bottom-sheet#2669).
            if (isPresentedRef.current) {
                bottomSheetModalRef.current?.dismiss();
            }
            setStatusBarDark();
        }
    }, [autoFocus, disabled, setStatusBarDark, setStatusBarLight, visible]);

    const keyExtractor = useCallback(
        (item: SelectItemType<V>) =>
            `${JSON.stringify(item.label)}-${JSON.stringify(item.description)}-${JSON.stringify(item.value)}`,
        [],
    );
    const getItem = useCallback((d: SelectItemType<V>[], index: number) => d[index], []);
    const getItemCount = useCallback((d: SelectItemType<V>[]) => d.length, []);

    const left = useMemo(() => {
        if (windowWidth <= MAX_BOTTOM_SHEET_WIDTH) return 0;

        return windowWidth / 2 - MAX_BOTTOM_SHEET_WIDTH / 2;
    }, [windowWidth]);

    const EmptyResultComponent = useMemo(
        () =>
            emptyResult ? (
                <VStack gap="1/3" v="2/3" h="2/3" alignment="center" style={{ paddingTop: 0 }}>
                    {emptyResult}
                </VStack>
            ) : undefined,
        [emptyResult],
    );

    const { height: newKeyboardHeight } = useKeyboardState();
    const { progress } = useReanimatedKeyboardAnimation();

    const isScreenFocused = useIsFocused();

    const animatedHstackPaddingBottom = useDerivedValue(
        () => 0.66 * gridSize + insets.bottom * (1 - progress.value),
    );

    const containerAnimatedStyle = useAnimatedStyle(() => ({
        paddingBottom: animatedHstackPaddingBottom.value,
    }));

    useEffect(() => {
        if (!isStaticItems) {
            setKeyboardStickyComponent({
                isVisible: visible && isScreenFocused,
                component: (
                    <AnimatedHStack
                        h="1/3"
                        gap="1/3"
                        wrapChildren={false}
                        style={[
                            {
                                left,
                                maxWidth: MAX_BOTTOM_SHEET_WIDTH,
                            },
                            containerAnimatedStyle,
                        ]}
                    >
                        <>
                            {keyboardStickyComponentLayout && !isLarge && (
                                <BackgroundBlur
                                    style={[
                                        {
                                            position: "absolute",
                                            width: keyboardStickyComponentLayout.width,
                                            maxWidth: MAX_BOTTOM_SHEET_WIDTH,
                                            flex: 1,
                                        },
                                    ]}
                                />
                            )}
                            <TextField
                                ref={textInput}
                                autoFocus={autoFocus}
                                style={{
                                    backgroundColor: colors.background,
                                    borderColor: colors.primary,
                                    borderRadius: 100,
                                    flex: 1,
                                }}
                                enterKeyHint="search"
                                placeholder={placeholder}
                                onChangeText={setSearchQuery}
                                value={searchQuery}
                                keyboardType={searchKeyboardType}
                                right={isSearching ? <Loading size="small" /> : undefined}
                            />
                        </>
                        <IconButton icon="close" mode="outlined" size="large" onPress={onClose} />
                    </AnimatedHStack>
                ),
                onLayout: (e) => {
                    if (!keyboardStickyComponentLayout) {
                        setKeyboardStickyComponentLayout(e.nativeEvent.layout);
                    }
                },
            });
        }
    }, [
        colors.background,
        colors.primary,
        isSearching,
        isStaticItems,
        isScreenFocused,
        keyboardStickyComponentLayout,
        onDismiss,
        placeholder,
        searchKeyboardType,
        searchQuery,
        setKeyboardStickyComponent,
        visible,
        left,
        isLarge,
        insets.bottom,
        containerAnimatedStyle,
        autoFocus,
        onClose,
    ]);

    const hasItems = useMemo(() => items.length > 0, [items.length]);

    const animationConfigs = useBottomSheetTimingConfigs({
        duration: KEYBOARD_STICKY_COMPONENT_ANIMATION_DELAY,
    });

    // @gorhom/bottom-sheet uses this as a component TYPE (`<BackdropComponent ... />`), and the
    // veil is a sibling rendered before the sheet's container. A new function identity is a new
    // type, so React tears the veil down and mounts a fresh one — which iOS appends after the
    // sheet's view, i.e. ON TOP of it. The sheet was then still there, still open, but behind a
    // dark veil that took every touch (BAS-2894, BAS-2918, BAS-2928): it happened on any re-render
    // of the calling screen — toggling a filter, or just leaving and re-entering the app.
    //
    // So this stays one and the same function, which `onClose` being stable is enough for.
    const renderBackdrop = useCallback(
        (props: BottomSheetBackdropProps) => (
            <BackdropComponent {...props} onBackdropPress={onClose} />
        ),
        [onClose],
    );

    const isDetached = isLarge && isStaticItems;

    return (
        <BottomSheetModal
            key={modalInstanceId}
            ref={bottomSheetModalRef}
            animatedIndex={sheetAnimatedIndex}
            containerComponent={bottomSheetContainerComponent}
            backdropComponent={renderBackdrop}
            animationConfigs={animationConfigs}
            onDismiss={handleModalDismiss}
            handleStyle={{
                paddingTop: DEFAULT_HANDLE_CONTAINER_PADDING,
                paddingBottom: DEFAULT_HANDLE_CONTAINER_PADDING,
            }}
            style={{
                left,
                maxWidth: MAX_BOTTOM_SHEET_WIDTH,
                borderRadius: roundness * 2,
                ...(!isDetached
                    ? {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                      }
                    : {}),
                overflow: "hidden",
            }}
            containerStyle={{
                opacity: hasItems ? 1 : 0,
            }}
            {...(isDetached
                ? {
                      detached: true,
                      bottomInset: roundness * 2,
                  }
                : {})}
            enablePanDownToClose
            maxDynamicContentSize={
                isLarge
                    ? windowHeight * 0.9
                    : windowHeight -
                      (topInsetExcludedFromWindow ? insets.top : 0) +
                      (!topInsetExcludedFromWindow ? insets.bottom : 0) // top inset is already deduced from windowHeight on Android
            }
            keyboardBehavior="interactive"
            keyboardBlurBehavior="restore"
        >
            <BottomSheetVirtualizedList
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={{
                    ...(items.length > 0 || (!showEmptytext && !isSearching)
                        ? {
                              paddingBottom:
                                  newKeyboardHeight +
                                  (keyboardStickyComponentLayout?.height || 0) +
                                  (isStaticItems ? insets.bottom : 0),
                          }
                        : {}),
                }}
                ListHeaderComponent={
                    topComponent ? (
                        <VStack
                            v="2/3"
                            h="2/3"
                            style={[
                                {
                                    backgroundColor: colors.background,
                                    paddingTop: 0,
                                    borderBottomWidth: 1,
                                    borderBottomColor: colors.lighterGrey,
                                },
                                topComponentStyle,
                            ]}
                        >
                            {topComponent}
                        </VStack>
                    ) : undefined
                }
                ListEmptyComponent={
                    !showEmptytext && !isSearching ? EmptyResultComponent : undefined
                }
                ListFooterComponent={
                    bottomComponent ? (
                        <VStack h="2/3" v="2/3" gap="1/3">
                            {bottomComponent}
                        </VStack>
                    ) : null
                }
                data={items}
                keyExtractor={keyExtractor}
                getItemCount={getItemCount}
                getItem={getItem}
                renderItem={rowRenderer}
            />
        </BottomSheetModal>
    );
};

const BackdropComponent = ({
    animatedIndex,
    animatedPosition,
    style,
    onBackdropPress,
}: BottomSheetBackdropProps & { onBackdropPress: () => void }) => {
    const opacity = useDerivedValue(() => (1 - -animatedIndex.value) / 2);

    // reanimated 4.3 returns an opaque AnimatedStyleHandle; cast for the non-animated backdrop style prop.
    const containerAnimatedStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
    })) as unknown as StyleProp<ViewStyle>;

    const containerStyle = useMemo(
        () => [
            style,
            {
                backgroundColor: `rgb(${BACKGROUND_RGB})`,
            },
        ],
        [style],
    );

    return (
        <BottomSheetBackdrop
            opacity={1}
            onPress={onBackdropPress}
            animatedIndex={animatedIndex}
            animatedPosition={animatedPosition}
            enableTouchThrough={false}
            style={[containerStyle, containerAnimatedStyle]}
            disappearsOnIndex={-1}
        />
    );
};
